import * as React from 'react';
import { motion, useAnimation } from 'motion/react';
import { useIsInView } from '../../../hooks/useIsInView';
import { Slot } from '../primitives/AnimateSlot';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

const staticAnimations = {
  path: {
    initial: { pathLength: 1 },
    animate: {
      pathLength: [0.05, 1],
      transition: { duration: 0.8, ease: 'easeInOut' },
    },
  },
  'path-loop': {
    initial: { pathLength: 1 },
    animate: {
      pathLength: [1, 0.05, 1],
      transition: { duration: 1.6, ease: 'easeInOut' },
    },
  },
};

const AnimateIconContext = React.createContext(null);

function useAnimateIconContext() {
  const context = React.useContext(AnimateIconContext);
  if (!context)
    return {
      controls: undefined,
      animation: 'default',
      loop: undefined,
      loopDelay: undefined,
      active: undefined,
      animate: undefined,
      initialOnAnimateEnd: undefined,
      completeOnStop: undefined,
      persistOnAnimateEnd: undefined,
      delay: undefined,
    };
  return context;
}

function composeEventHandlers(theirs, ours) {
  return (event) => {
    theirs?.(event);
    ours?.(event);
  };
}

function AnimateIcon({
  asChild = false,
  animate = false,
  animateOnHover = false,
  animateOnTap = false,
  animateOnView = false,
  animateOnViewMargin = '0px',
  animateOnViewOnce = true,
  animation = 'default',
  loop = false,
  loopDelay = 0,
  initialOnAnimateEnd = false,
  completeOnStop = false,
  persistOnAnimateEnd = false,
  delay = 0,
  children,
  ...props
}) {
  const controls = useAnimation();

  const [localAnimate, setLocalAnimate] = React.useState(() => {
    if (animate === undefined || animate === false) return false;
    return delay <= 0;
  });
  const [currentAnimation, setCurrentAnimation] = React.useState(
    typeof animate === 'string' ? animate : animation,
  );
  const [status, setStatus] = React.useState('initial');

  const delayRef = React.useRef(null);
  const loopDelayRef = React.useRef(null);
  const isAnimateInProgressRef = React.useRef(false);
  const animateEndPromiseRef = React.useRef(null);
  const resolveAnimateEndRef = React.useRef(null);
  const activeRef = React.useRef(localAnimate);
  const runGenRef = React.useRef(0);
  const cancelledRef = React.useRef(false);

  const bumpGeneration = React.useCallback(() => { runGenRef.current++; }, []);

  const startAnimation = React.useCallback((trigger) => {
    const next = typeof trigger === 'string' ? trigger : animation;
    bumpGeneration();
    if (delayRef.current) { clearTimeout(delayRef.current); delayRef.current = null; }
    setCurrentAnimation(next);
    if (delay > 0) {
      setLocalAnimate(false);
      delayRef.current = setTimeout(() => setLocalAnimate(true), delay);
    } else {
      setLocalAnimate(true);
    }
  }, [animation, delay, bumpGeneration]);

  const stopAnimation = React.useCallback(() => {
    bumpGeneration();
    if (delayRef.current) { clearTimeout(delayRef.current); delayRef.current = null; }
    if (loopDelayRef.current) { clearTimeout(loopDelayRef.current); loopDelayRef.current = null; }
    setLocalAnimate(false);
  }, [bumpGeneration]);

  React.useEffect(() => { activeRef.current = localAnimate; }, [localAnimate]);

  React.useEffect(() => {
    if (animate === undefined) return;
    setCurrentAnimation(typeof animate === 'string' ? animate : animation);
    if (animate) startAnimation(animate);
    else stopAnimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animate]);

  React.useEffect(() => {
    return () => {
      if (delayRef.current) clearTimeout(delayRef.current);
      if (loopDelayRef.current) clearTimeout(loopDelayRef.current);
    };
  }, []);

  const viewOuterRef = React.useRef(null);
  const { ref: inViewRef, isInView } = useIsInView(viewOuterRef, {
    inView: !!animateOnView,
    inViewOnce: animateOnViewOnce,
    inViewMargin: animateOnViewMargin,
  });

  const startAnim = React.useCallback(async (anim, method = 'start') => {
    try {
      await controls[method](anim);
      setStatus(anim);
    } catch {
      return;
    }
  }, [controls]);

  React.useEffect(() => {
    if (!animateOnView) return;
    if (isInView) startAnimation(animateOnView);
    else stopAnimation();
  }, [isInView, animateOnView, startAnimation, stopAnimation]);

  React.useEffect(() => {
    const gen = ++runGenRef.current;
    cancelledRef.current = false;

    async function run() {
      if (cancelledRef.current || gen !== runGenRef.current) {
        await startAnim('initial'); return;
      }
      if (!localAnimate) {
        if (completeOnStop && isAnimateInProgressRef.current && animateEndPromiseRef.current) {
          try { await animateEndPromiseRef.current; } catch {}
        }
        if (!persistOnAnimateEnd) {
          if (cancelledRef.current || gen !== runGenRef.current) { await startAnim('initial'); return; }
          await startAnim('initial');
        }
        return;
      }
      if (loop) {
        if (cancelledRef.current || gen !== runGenRef.current) { await startAnim('initial'); return; }
        await startAnim('initial', 'set');
      }
      isAnimateInProgressRef.current = true;
      animateEndPromiseRef.current = new Promise((resolve) => { resolveAnimateEndRef.current = resolve; });
      if (cancelledRef.current || gen !== runGenRef.current) {
        isAnimateInProgressRef.current = false;
        resolveAnimateEndRef.current?.();
        resolveAnimateEndRef.current = null;
        animateEndPromiseRef.current = null;
        await startAnim('initial'); return;
      }
      await startAnim('animate');
      if (cancelledRef.current || gen !== runGenRef.current) {
        isAnimateInProgressRef.current = false;
        resolveAnimateEndRef.current?.();
        resolveAnimateEndRef.current = null;
        animateEndPromiseRef.current = null;
        await startAnim('initial'); return;
      }
      isAnimateInProgressRef.current = false;
      resolveAnimateEndRef.current?.();
      resolveAnimateEndRef.current = null;
      animateEndPromiseRef.current = null;
      if (initialOnAnimateEnd) {
        if (cancelledRef.current || gen !== runGenRef.current) { await startAnim('initial'); return; }
        await startAnim('initial', 'set');
      }
      if (loop) {
        if (loopDelay > 0) {
          await new Promise((resolve) => {
            loopDelayRef.current = setTimeout(() => { loopDelayRef.current = null; resolve(); }, loopDelay);
          });
          if (cancelledRef.current || gen !== runGenRef.current) { await startAnim('initial'); return; }
          if (!activeRef.current) {
            if (status !== 'initial' && !persistOnAnimateEnd) await startAnim('initial');
            return;
          }
        } else {
          if (!activeRef.current) {
            if (status !== 'initial' && !persistOnAnimateEnd) await startAnim('initial');
            return;
          }
        }
        if (cancelledRef.current || gen !== runGenRef.current) { await startAnim('initial'); return; }
        await run();
      }
    }

    void run();

    return () => {
      cancelledRef.current = true;
      if (delayRef.current) { clearTimeout(delayRef.current); delayRef.current = null; }
      if (loopDelayRef.current) { clearTimeout(loopDelayRef.current); loopDelayRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localAnimate, controls]);

  const childProps = React.isValidElement(children) ? children.props : {};

  const handleMouseEnter = composeEventHandlers(childProps.onMouseEnter, () => {
    if (animateOnHover) startAnimation(animateOnHover);
  });
  const handleMouseLeave = composeEventHandlers(childProps.onMouseLeave, () => {
    if (animateOnHover || animateOnTap) stopAnimation();
  });
  const handlePointerDown = composeEventHandlers(childProps.onPointerDown, () => {
    if (animateOnTap) startAnimation(animateOnTap);
  });
  const handlePointerUp = composeEventHandlers(childProps.onPointerUp, () => {
    if (animateOnTap) stopAnimation();
  });

  const content = asChild ? (
    <Slot
      ref={inViewRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      {...props}
    >
      {children}
    </Slot>
  ) : (
    <motion.span
      ref={inViewRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      {...props}
    >
      {children}
    </motion.span>
  );

  return (
    <AnimateIconContext.Provider
      value={{
        controls,
        animation: currentAnimation,
        loop,
        loopDelay,
        active: localAnimate,
        animate,
        initialOnAnimateEnd,
        completeOnStop,
        delay,
      }}
    >
      {content}
    </AnimateIconContext.Provider>
  );
}

const pathClassName = "[&_[stroke-dasharray='1px_1px']]:![stroke-dasharray:1px_0px]";

function IconWrapper({
  size = 28,
  animation: animationProp,
  animate,
  animateOnHover,
  animateOnTap,
  animateOnView,
  animateOnViewMargin,
  animateOnViewOnce,
  icon: IconComponent,
  loop,
  loopDelay,
  persistOnAnimateEnd,
  initialOnAnimateEnd,
  delay,
  completeOnStop,
  className,
  ...props
}) {
  const context = React.useContext(AnimateIconContext);

  if (context) {
    const {
      controls,
      animation: parentAnimation,
      loop: parentLoop,
      loopDelay: parentLoopDelay,
      active: parentActive,
      animate: parentAnimate,
      persistOnAnimateEnd: parentPersistOnAnimateEnd,
      initialOnAnimateEnd: parentInitialOnAnimateEnd,
      delay: parentDelay,
      completeOnStop: parentCompleteOnStop,
    } = context;

    const hasOverrides =
      animate !== undefined ||
      animateOnHover !== undefined ||
      animateOnTap !== undefined ||
      animateOnView !== undefined ||
      loop !== undefined ||
      loopDelay !== undefined ||
      initialOnAnimateEnd !== undefined ||
      persistOnAnimateEnd !== undefined ||
      delay !== undefined ||
      completeOnStop !== undefined;

    if (hasOverrides) {
      const inheritedAnimate = parentActive
        ? (animationProp ?? parentAnimation ?? 'default')
        : false;
      const finalAnimate = animate ?? parentAnimate ?? inheritedAnimate;

      return (
        <AnimateIcon
          animate={finalAnimate}
          animateOnHover={animateOnHover}
          animateOnTap={animateOnTap}
          animateOnView={animateOnView}
          animateOnViewMargin={animateOnViewMargin}
          animateOnViewOnce={animateOnViewOnce}
          animation={animationProp ?? parentAnimation}
          loop={loop ?? parentLoop}
          loopDelay={loopDelay ?? parentLoopDelay}
          persistOnAnimateEnd={persistOnAnimateEnd ?? parentPersistOnAnimateEnd}
          initialOnAnimateEnd={initialOnAnimateEnd ?? parentInitialOnAnimateEnd}
          delay={delay ?? parentDelay}
          completeOnStop={completeOnStop ?? parentCompleteOnStop}
          asChild
        >
          <IconComponent
            size={size}
            className={cn(className)}
            {...props}
          />
        </AnimateIcon>
      );
    }

    const animationToUse = animationProp ?? parentAnimation;
    return (
      <AnimateIconContext.Provider
        value={{
          controls,
          animation: animationToUse,
          loop: parentLoop,
          loopDelay: parentLoopDelay,
          active: parentActive,
          animate: parentAnimate,
          initialOnAnimateEnd: parentInitialOnAnimateEnd,
          delay: parentDelay,
          completeOnStop: parentCompleteOnStop,
        }}
      >
        <IconComponent size={size} className={cn(className)} {...props} />
      </AnimateIconContext.Provider>
    );
  }

  if (
    animate !== undefined ||
    animateOnHover !== undefined ||
    animateOnTap !== undefined ||
    animateOnView !== undefined ||
    animationProp !== undefined
  ) {
    return (
      <AnimateIcon
        animate={animate}
        animateOnHover={animateOnHover}
        animateOnTap={animateOnTap}
        animateOnView={animateOnView}
        animateOnViewMargin={animateOnViewMargin}
        animateOnViewOnce={animateOnViewOnce}
        animation={animationProp}
        loop={loop}
        loopDelay={loopDelay}
        delay={delay}
        completeOnStop={completeOnStop}
        asChild
      >
        <IconComponent size={size} className={cn(className)} {...props} />
      </AnimateIcon>
    );
  }

  return <IconComponent size={size} className={cn(className)} {...props} />;
}

function getVariants(animations) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { animation: animationType } = useAnimateIconContext();

  if (animationType in staticAnimations) {
    const variant = staticAnimations[animationType];
    const result = {};
    for (const key in animations.default) {
      if ((animationType === 'path' || animationType === 'path-loop') && key.includes('group')) continue;
      result[key] = variant;
    }
    return result;
  }

  return animations[animationType] ?? animations.default;
}

export {
  AnimateIcon,
  IconWrapper,
  useAnimateIconContext,
  getVariants,
};
